import { Group, Stack, Text } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import {
  getCoreRowModel,
  PaginationState,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table"
import {
  ExportButton,
  ExportType,
  InfoBox,
  Loader,
  SectionHeader,
} from "@tfg/ui"
import _ from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { filtersSchema } from "@tfg/types/schemas/products"

import { ProductDuplicateProductionSiteFilter } from "~/components/ProductDuplicateProductionSiteFilter"
import { ProductFilters } from "~/components/ProductFilters.tsx"
import { ProductIncomingLaunchDateFilter } from "~/components/ProductIncomingLanchingDateFilter"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { Table } from "~/components/table/Table.tsx"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/OrganizationSettings/ProductPages/hooks.ts"
import { ProductStandardFilter } from "~/pages/Brand/Products/List/Components/ProductStandardFilter"
import { StandardComplianceDrawer } from "~/pages/Brand/Products/List/Components/StandardDrawer.tsx"
import { useColumns } from "~/pages/Brand/Products/List/Components/columns.tsx"
import { useHeaders } from "~/pages/Brand/Products/List/Components/headers.tsx"
import { useColumnVisibility } from "~/pages/Brand/Products/List/Services/useColumnVisibility.tsx"
import { ProductsListView } from "~/pages/Brand/Products/List/constants.ts"
import {
  useDownloadProductsCertificatesCSVMutation,
  useDownloadProductsCSVMutation,
  useProductsQuery,
} from "~/pages/Brand/Products/List/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

const PAGE_SIZE = 20

export const ProductsTable = () => {
  const params = useParams()
  const view = useMemo(
    () => (_.camelCase(params.view) || "general") as ProductsListView,
    [params]
  )

  const [showOnlyMainFabric, setShowOnlyMainFabric] = useState<boolean>(false)
  const { t } = useTranslation(["products", "common"])

  const { user, isAdmin } = useAuth()

  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const [filtersValue, setFiltersValue] = useSearchParamsFilters(filtersSchema)

  const [debouncedSearch] = useDebouncedValue<string | undefined>(
    filtersValue.search,
    600
  )

  const [pageCount, setPageCount] = useState<number>(0)
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const fetchDataOptions = {
    pageIndex,
    pageSize,
  }
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
  } = useProductsQuery({
    fetchDataOptions,
    filters: { ...filtersValue, search: debouncedSearch },
  })

  useEffect(() => {
    if (productsData?.totalProducts && productsData?.totalProducts === 0) {
      setPageCount(0)
    }
    if (productsData?.totalProducts && productsData?.totalProducts) {
      setPageCount(Math.ceil(productsData?.totalProducts / pageSize))
    }
  }, [productsData?.totalProducts, pageSize])

  const products = useMemo(() => {
    return productsData?.products ?? []
  }, [productsData])
  const deepestRank = _.max(products.map((p) => p.maxRank)) ?? 0

  const { mutate: exportAll, isLoading: isProductExportLoading } =
    useDownloadProductsCSVMutation({
      filters: { ...filtersValue, search: debouncedSearch },
    })

  const { standardHeaders, customHeaders } = useHeaders(
    products,
    view,
    filtersValue?.standardId
  )
  const [selectedStandard, setSelectedStandard] = useState<
    LibraryResponses.ProductStandard | undefined
  >(undefined)

  const columns = useColumns(
    deepestRank,
    showOnlyMainFabric,
    Object.keys(customHeaders),
    standardHeaders,
    setSelectedStandard
  )

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const { columnVisibility, setColumnVisibility } = useColumnVisibility({
    view,
    products,
    customHeaders,
    standardHeaders,
  })

  const table = useReactTable({
    data: products,
    columns,
    state: {
      pagination,
      columnVisibility,
      columnPinning: { left: ["select"] },
      rowSelection,
    },
    pageCount,
    manualPagination: true,
    enableRowSelection: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [debouncedSearch, filtersValue])

  const {
    mutate: exportCertificateView,
    isLoading: isCertificatesExportLoading,
  } = useDownloadProductsCertificatesCSVMutation({
    filters: { ...filtersValue, search: debouncedSearch },
  })
  let adminMessage: string | null = null
  if (
    (view === "traceability" && org?.settings?.zalandoPlan === "ENABLED") ||
    (view === "customFields" && org?.settings?.customFieldsPlan !== "EXPERT") ||
    (view === "certificates" && org?.settings?.certificatesPlan !== "EXPERT")
  ) {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("simulation.notSubscribed.title")}
          message={t("simulation.notSubscribed.message")}
          color="blue"
        />
      )
    }
  }

  if (isLoadingProducts) {
    return <Loader fullPage />
  }

  const exportCta =
    view === "general" ? (
      <ExportButton
        label={t("lists.export.all")}
        exportOptions={[
          {
            type: ExportType.CSV,
            label: t("common:actionButtons.exportAsCsv"),
            exportFunction: exportAll,
            isLoading: isProductExportLoading,
          },
        ]}
      />
    ) : view === "certificates" ? (
      <ExportButton
        label={t("lists.export.view")}
        exportOptions={[
          {
            type: ExportType.CSV,
            label: t("common:actionButtons.exportAsCsv"),
            exportFunction: exportCertificateView,
            isLoading: isCertificatesExportLoading,
          },
        ]}
      />
    ) : null

  return (
    <Stack>
      {adminMessage && (
        <Text c="violet" size="xs">
          {adminMessage}
        </Text>
      )}
      <SectionHeader title={t(`lists.tabs.${view}`)} mainCta={exportCta} />
      <Group>
        <ProductFilters
          setFiltersValue={setFiltersValue}
          filtersValue={filtersValue}
          isFetching={isFetchingProducts}
          extraFilters={
            <>
              {view === "certificates" ? (
                <ProductStandardFilter
                  filtersValue={filtersValue}
                  setFiltersValue={setFiltersValue}
                />
              ) : undefined}
              {view === "traceability" ? (
                <ProductDuplicateProductionSiteFilter
                  filtersValue={filtersValue}
                  setFiltersValue={setFiltersValue}
                />
              ) : undefined}
              {view === "general" ? (
                <ProductIncomingLaunchDateFilter
                  filtersValue={filtersValue}
                  setFiltersValue={setFiltersValue}
                />
              ) : undefined}
            </>
          }
        />
      </Group>
      <Table
        table={table}
        noColumnWrap={false}
        view={view}
        showOnlyMainFabric={showOnlyMainFabric}
        setShowOnlyMainFabric={setShowOnlyMainFabric}
        manualPaginationCount={productsData?.totalProducts || 0}
        pageIndex={pageIndex}
      />
      {selectedStandard && (
        <StandardComplianceDrawer
          standard={selectedStandard}
          onClose={() => setSelectedStandard(undefined)}
        />
      )}
    </Stack>
  )
}
